// TS twin of iOS `ManualAdjustmentPanel.swift`.
// Bottom card composing the JoystickPad (XY + depth) and two DragStrips
// (camber + rotate) + a delete button. Portrait stacks card + strips
// around the joystick; landscape rotates the joystick above the strips.

import { Trash2 } from 'lucide-react'
import type { WheelId } from '@/ar/wheel-renderer'
import { MANUAL_ADJUSTMENT_STEPS } from '@/models/placement-state'
import { JoystickPad } from '@/components/joystick-pad/joystick-pad'
import { DragStrip } from '@/components/drag-strip/drag-strip'
import {
  WheelSelector,
  CHIP_OUTLINE_PX,
} from '@/components/wheel-selector/wheel-selector'

/** Same as the camera / close CircleIconButtons in TopControlsView. */
const TOUCH_UI_BUTTON_SIZE = 39

/** Delete wears the chips' outline weight in red. */
const DELETE_OUTLINE_COLOR = 'rgba(255, 90, 90, 0.95)'

/** The dark disc every touchUi control sits on. */
const DARK_DISC_BG = 'rgba(0, 0, 0, 0.85)'

interface ManualAdjustmentPanelProps {
  /** Null is a real state under ?touchUi: the panel is fixed furniture
   *  that outlives selection, so it renders with nothing selected — no
   *  chip highlighted, and delete with nothing to act on. */
  wheelId: WheelId | null
  isLandscape: boolean
  /** ?touchUi — the wheel itself is the control, so the joystick and
   *  the camber / rotate strips come out. What stays is the row with no
   *  direct-manipulation equivalent: select, add, delete, configs. You
   *  cannot pinch your way to "add a second wheel". */
  touchUi?: boolean
  /** A placement is armed — the nav shows the pending wheel's chip
   *  rather than another "+", and the trash cancels it. */
  placementPending?: boolean
  onHeightChange: (delta: number) => void
  onDepthChange: (delta: number) => void
  onHorizontalChange: (delta: number) => void
  onTiltChange: (delta: number) => void
  onYawChange: (delta: number) => void
  onScaleChange: (delta: number) => void
  onDelete: () => void
  /** Current camber in radians (component displays converted degrees). */
  currentTiltAngle?: number
  /** Current yaw in radians (component displays converted degrees). */
  currentYawAngle?: number
  /** Current depth offset (m) — displayed in inches when the JoystickPad
   *  is in depth mode. */
  currentDepth?: number
  /** Current scale multiplier — displayed as % delta when in scale mode. */
  currentScale?: number
  /** Nominal wheel diameter (inches) parsed from the loader — passed
   *  through to the joystick's scale readout. */
  originalDiameterInches?: number
  /** Placed-wheel ordering for the (1)(2)... chip strip. Empty array
   *  hides the strip; length ≥ 1 shows chips + add-button (until
   *  length reaches maxWheels). */
  placedWheelIds?: readonly WheelId[]
  maxWheels?: number
  onSelectWheel?: (id: WheelId) => void
  onAddWheel?: () => void
}

const DRAG_SENSITIVITY = 0.005
const RAD_TO_DEG = 180 / Math.PI

export function ManualAdjustmentPanel({
  wheelId,
  isLandscape,
  touchUi = false,
  placementPending = false,
  onHeightChange,
  onDepthChange,
  onHorizontalChange,
  onTiltChange,
  onYawChange,
  onScaleChange,
  onDelete,
  currentTiltAngle = 0,
  currentYawAngle = 0,
  currentDepth = 0,
  currentScale = 1,
  originalDiameterInches = 26,
  placedWheelIds = [],
  maxWheels = 4,
  onSelectWheel,
  onAddWheel,
}: ManualAdjustmentPanelProps) {
  const step = MANUAL_ADJUSTMENT_STEPS

  const joystick = (
    <JoystickPad
      onUp={() => onHeightChange(step.height)}
      onDown={() => onHeightChange(-step.height)}
      onLeft={() => onHorizontalChange(step.horizontal)}
      onRight={() => onHorizontalChange(-step.horizontal)}
      onDepthDrag={onDepthChange}
      depthStep={step.depth}
      onScaleChange={onScaleChange}
      scaleStep={step.scale}
      currentDepth={currentDepth}
      currentScale={currentScale}
      originalDiameterInches={originalDiameterInches}
    />
  )

  const camberStrip = (
    <DragStrip
      testId="drag-strip-camber"
      icon="∠"
      label="Camber"
      sensitivity={DRAG_SENSITIVITY}
      onDrag={onTiltChange}
      currentAngle={currentTiltAngle * RAD_TO_DEG}
      compact={isLandscape}
    />
  )

  const rotateStrip = (
    <DragStrip
      testId="drag-strip-rotate"
      icon="↔"
      label="Rotate"
      sensitivity={DRAG_SENSITIVITY}
      onDrag={onYawChange}
      currentAngle={currentYawAngle * RAD_TO_DEG}
      compact={isLandscape}
    />
  )

  const deleteButton = (
    <button
      type="button"
      data-testid="delete-wheel-button"
      onClick={onDelete}
      aria-label="Delete wheel"
      style={{
        // Under touchUi the trash joins the wheel controls: same 39px
        // as the camera / close buttons and the chips, and round for
        // the same reason they are.
        //
        // border-box so the 3px outline paints INWARD. Nothing in this
        // app sets box-sizing, so under the default content-box this
        // button rendered 6px wider than the chips it sits beside
        // (2026-08-24 LUAT).
        boxSizing: 'border-box',
        width: touchUi ? TOUCH_UI_BUTTON_SIZE : 23,
        height: touchUi ? TOUCH_UI_BUTTON_SIZE : 23,
        borderRadius: touchUi ? 999 : 8,
        // Same contrast treatment as the wheel chips, in red: a 3px
        // outline over a dark disc. The old translucent-white fill was
        // invisible over grass (2026-08-24 LUAT).
        border: touchUi ? `${CHIP_OUTLINE_PX}px solid ${DELETE_OUTLINE_COLOR}` : 'none',
        background: touchUi ? DARK_DISC_BG : 'rgba(255, 255, 255, 0.1)',
        color: touchUi ? DELETE_OUTLINE_COLOR : 'rgba(255, 80, 80, 0.85)',
        cursor: 'pointer',
        position: 'absolute',
        top: 8,
        right: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Trash2
        size={touchUi ? Math.round(TOUCH_UI_BUTTON_SIZE * 0.55) : 14}
        strokeWidth={2}
      />
    </button>
  )

  return (
    <section
      data-wheel-id={wheelId}
      data-orientation={isLandscape ? 'landscape' : 'portrait'}
      style={{
        position: 'relative',
        // ?touchUi: no slab. The buttons carry their own dark chips, so
        // a panel background behind them only masks the AR view the
        // user is reaching into (2026-08-23 LUAT).
        background: touchUi ? 'transparent' : 'rgba(0, 0, 0, 0.85)',
        // The bare container passes taps through to the AR view, so the
        // panel takes them back for its own buttons.
        ...(touchUi ? { pointerEvents: 'auto' as const } : {}),
        borderRadius: touchUi ? 0 : isLandscape ? 16 : 20,
        padding: touchUi ? 8 : isLandscape ? 12 : '12px 8px',
        color: 'white',
      }}
    >
      {/* The trash needs something to act on: a selected wheel, or a
          placement in progress it can cancel. Under touchUi the panel is
          permanent furniture, so without this gate the icon sat over an
          empty scene doing nothing (2026-08-23 LUAT). */}
      {(!touchUi || wheelId != null || placementPending) && deleteButton}
      {placedWheelIds.length > 0 &&
        onSelectWheel != null &&
        onAddWheel != null && (
          <WheelSelector
            wheelIds={placedWheelIds}
            selectedId={wheelId}
            maxWheels={maxWheels}
            onSelect={onSelectWheel}
            onAdd={onAddWheel}
            // Match the camera / close buttons (39px) — with the
            // joystick gone these are the primary controls, not a
            // secondary strip tucked under it.
            size={touchUi ? TOUCH_UI_BUTTON_SIZE : undefined}
            selectedOutline
            round={touchUi}
            pendingChip={placementPending}
          />
        )}
      {touchUi ? null : isLandscape ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
          {joystick}
          <div style={{ display: 'flex', gap: 6 }}>
            {camberStrip}
            {rotateStrip}
          </div>
        </div>
      ) : (
        <>
          <div
            aria-hidden
            style={{
              width: 36,
              height: 5,
              background: 'rgba(255, 255, 255, 0.4)',
              borderRadius: 2.5,
              margin: '8px auto',
            }}
          />
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '12px 8px',
            }}
          >
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {camberStrip}
            </div>
            {joystick}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              {rotateStrip}
            </div>
          </div>
        </>
      )}
    </section>
  )
}
